import { useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { Modal } from "antd-mobile";
import PageWrap from "../components/PageWrap";
import PageHeader, { headerHeight } from "../components/PageHeader";
import { useStores } from "../../models";
import { ToastError } from "../../components/Toast";

const bg = require("../../assets/images/wallet/creditrating-bg.jpeg");

const ScrollArea = styled.div`
  height: calc(100% - ${headerHeight});
  overflow: auto;
  background: #f7f8ff;
`;

const Hero = styled.div`
  height: 293px;
  background: url(${bg}) center/cover no-repeat;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Score = styled.div`
  font-size: 64px;
  color: #006bff;
  font-weight: 400;
  line-height: 1;
`;

const Tag = styled.div`
  margin-top: 8px;
  min-width: 88px;
  height: 31px;
  border-radius: 20px;
  background: #ffee6d;
  color: #cc5b04;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
`;

const Panel = styled.div`
  margin: -26px 12px 16px;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  position: relative;
`;

const Title = styled.div`
  margin: 12px 0;
  font-size: 15px;
  font-weight: 600;
  color: #222;
  line-height: 18px;
`;

const SubText = styled.div`
  margin: 12px 0;
  font-size: 14px;
  color: #222;
  line-height: 22px;
`;

const Desc = styled.div`
  margin: 10px 0;
  font-size: 14px;
  color: #7f7f7f;
  line-height: 20px;
`;

const LinkText = styled.span`
  color: #1c9cea;
  cursor: pointer;
`;

const HtmlBody = styled.div`
  max-height: 60vh;
  overflow: auto;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  text-align: left;
`;

const getLevel = (score: number) => {
  if (score < 550) return "信用极差";
  if (score < 600) return "信用中等";
  if (score < 650) return "信用良好";
  if (score < 700) return "信用优秀";
  return "信用极好";
};

const CreditRating = () => {
  const { walletStore, apiStore } = useStores();
  const score = useMemo(
    () => Number(walletStore.credit_score) || 0,
    [walletStore.credit_score]
  );
  const [visible, setVisible] = useState(false);
  const [title, setTitle] = useState("");
  const [html, setHtml] = useState("");

  const getRatingRule = async () => {
    try {
      setVisible(true);
      setTitle("评分规则");
      setHtml("");
      const res = await apiStore.getRatingRule();
      const value = res?.value ?? res?.data?.value ?? "";
      if (value) setHtml(value);
    } catch (e: any) {
      ToastError(e?.message || "加载失败");
    }
  };

  const getCreditScore = async () => {
    try {
      setVisible(true);
      setTitle("信用分规则");
      setHtml("");
      const res = await apiStore.getCreditRule();
      const value = res?.value ?? res?.data?.value ?? "";
      if (value) setHtml(value);
    } catch (e: any) {
      ToastError(e?.message || "加载失败");
    }
  };

  return (
    <PageWrap>
      <PageHeader title="信用等级" />
      <ScrollArea>
        <Hero>
          <Score>{score || "0"}</Score>
          <Tag>{getLevel(score)}</Tag>
        </Hero>
        <Panel>
          <Title>什么是信用分？</Title>
          <SubText>信用分是对个人的综合评分，主要由以下信息评估得出:</SubText>
          <Desc>身份认证：完善实名信息以及支付信息</Desc>
          <Desc>
            交易行为：交易时按时完成、未超时处理订单，较高的交易次数也有助于信用分评估
          </Desc>
          <Desc>
            守约记录：不同场景的履约记录，逾期交易或频繁关闭订单将严重影响信用分
          </Desc>
          <Desc>
            用户评分：买卖双方在单笔交易时，根据交易结果进行评分，根据
            <LinkText onClick={getRatingRule}>评分规则</LinkText>
            加减分
          </Desc>

          <Title>信用分过低会？</Title>
          <Desc>
            当您的信用分过低时系统会限制每日交易次数，额度上限。
            <LinkText onClick={getCreditScore}>信用分规则</LinkText>
          </Desc>

          <Title>信用分等级划分</Title>
          <Desc>
            600-800为信用优秀，每日交易次数上限为50次，每次交易额度上限为10,000元，头像旁有专属信用评分身份标识
          </Desc>
          <Desc>
            400-599为信用良好，每日交易次数上限为20次，每次交易额度上限为5,000元，头像旁有专属信用评分身份标识
          </Desc>
          <Desc>
            200-399为信用一般，每日交易次数上限为10次，每次交易额度上限为1,000，头像旁有专属信用评分身份标识
          </Desc>
          <Desc>
            0-199为信用一般，每日交易次数上限为1次，每次交易额度上限为200，头像旁有专属信用评分身份标识
          </Desc>
        </Panel>
      </ScrollArea>

      <Modal
        visible={visible}
        title={title}
        content={
          <HtmlBody
            dangerouslySetInnerHTML={{
              __html: html || "<div style='color:#999'>暂无内容</div>",
            }}
          />
        }
        closeOnAction
        onClose={() => setVisible(false)}
        actions={[{ key: "ok", text: "关闭" }]}
      />
    </PageWrap>
  );
};

export default observer(CreditRating);
